import { computed, onBeforeUnmount, onMounted, ref, unref, watch, type ComputedRef, type Ref } from 'vue'
import type { BoostOpportunityChartItem } from '@/composables/useBoostOpportunityNavigation'

export const BOOST_OPPORTUNITY_WINDOW_DAYS = 30

const BREAKPOINT_MD = 768
const BREAKPOINT_LG = 1024

export const getWindowDaysForWidth = (width: number): number => {
  if (width < BREAKPOINT_MD) return 10
  if (width < BREAKPOINT_LG) return 15

  return BOOST_OPPORTUNITY_WINDOW_DAYS
}

const parseDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number)

  return new Date(year, month - 1, day)
}

const formatDateString = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const addDays = (dateStr: string, days: number): string => {
  const date = parseDate(dateStr)
  date.setDate(date.getDate() + days)

  return formatDateString(date)
}

const toUTCDayTimestamp = (date: Date): number => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())

const calendarSpanDays = (startDateStr: string, endDateStr: string): number => {
  if (!startDateStr || !endDateStr) return 0

  const start = parseDate(startDateStr)
  const end = parseDate(endDateStr)

  if (end < start) return 0

  const msPerDay = 1000 * 60 * 60 * 24

  return Math.floor((toUTCDayTimestamp(end) - toUTCDayTimestamp(start)) / msPerDay) + 1
}

export function useBoostOpportunityChartWindow(
  chartData: Ref<BoostOpportunityChartItem[]> | ComputedRef<BoostOpportunityChartItem[]> | BoostOpportunityChartItem[]
) {
  const windowIndex = ref(0)
  const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : BREAKPOINT_LG)

  const windowDays = computed(() => getWindowDaysForWidth(windowWidth.value))

  const sortedChartData = computed(() => {
    const data = unref(chartData) || []

    return [...data].sort((a, b) => a.searchedCheckinDate.localeCompare(b.searchedCheckinDate))
  })

  const todayDate = computed(() => formatDateString(new Date()))

  const rangeEndDate = computed(() => {
    const data = sortedChartData.value

    return data[data.length - 1]?.searchedCheckinDate ?? todayDate.value
  })

  const calendarRangeDays = computed(() => calendarSpanDays(todayDate.value, rangeEndDate.value))

  const maxWindowIndex = computed(() => Math.max(0, Math.ceil(calendarRangeDays.value / windowDays.value) - 1))

  const windowStartDate = computed(() => addDays(todayDate.value, windowIndex.value * windowDays.value))

  const windowEndDate = computed(() => addDays(windowStartDate.value, windowDays.value - 1))

  const visibleChartData = computed(() => {
    return sortedChartData.value.filter(
      item => item.searchedCheckinDate >= windowStartDate.value && item.searchedCheckinDate <= windowEndDate.value
    )
  })

  const canGoPrevious = computed(() => windowIndex.value > 0)
  const canGoNext = computed(() => windowIndex.value < maxWindowIndex.value)
  const showDateFilter = computed(() => sortedChartData.value.length > 0)

  const goToPreviousWindow = () => {
    if (canGoPrevious.value) {
      windowIndex.value -= 1
    }
  }

  const goToNextWindow = () => {
    if (canGoNext.value) {
      windowIndex.value += 1
    }
  }

  const onResize = () => {
    windowWidth.value = window.innerWidth
  }

  onMounted(() => {
    windowWidth.value = window.innerWidth
    window.addEventListener('resize', onResize)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onResize)
  })

  watch(
    () => sortedChartData.value.map(item => item.searchedCheckinDate).join(','),
    () => {
      windowIndex.value = 0
    }
  )

  watch(maxWindowIndex, value => {
    if (windowIndex.value > value) {
      windowIndex.value = value
    }
  })

  return {
    visibleChartData,
    windowStartDate,
    windowEndDate,
    canGoPrevious,
    canGoNext,
    showDateFilter,
    goToPreviousWindow,
    goToNextWindow,
  }
}
